import { computed, type ComputedRef } from 'vue';
import { storeToRefs } from 'pinia';
import { useEntitlementsStore } from '@/store/useEntitlementsStore';
import type { EntitlementCapabilities, EntitlementLimits, EntitlementQuotas, QuotaValue } from '@/types/entitlements';

/**
 * Composable for accessing entitlements in Vue components.
 *
 * Provides reactive access to capabilities, limits, and quotas with
 * helper methods for common entitlement checks.
 *
 * @example
 * ```vue
 * <script setup>
 * const { can, limit, quota, quotaAllows, fetchEntitlements } = useEntitlements();
 *
 * // Check capability
 * const hasSso = can("sso");
 *
 * // Get limit
 * const maxVideoLength = limit("maxVideoLength");
 *
 * // Check quota
 * const canUpload = quotaAllows("monthlyUploads");
 *
 * // Fetch on mount
 * onMounted(() => fetchEntitlements());
 * </script>
 * ```
 */
export function useEntitlements() {
  const store = useEntitlementsStore();
  const { capabilities, limits, quotas, isLoaded, isLoading, error } = storeToRefs(store);

  /**
   * Check if user has a specific capability.
   *
   * @param key - The capability key to check
   * @returns Computed ref that is true if user has the capability
   */
  const can = (key: keyof EntitlementCapabilities): ComputedRef<boolean> => {
    return computed(() => capabilities.value[key] === true);
  };

  /**
   * Get a specific limit value.
   *
   * @param key - The limit key to get
   * @returns Computed ref with the limit value (null = unlimited)
   */
  const limit = (key: keyof EntitlementLimits): ComputedRef<number | null | undefined> => {
    return computed(() => limits.value[key]);
  };

  /**
   * Get a specific quota with used/remaining values.
   *
   * @param key - The quota key to get
   * @returns Computed ref with the quota object
   */
  const quota = (key: keyof EntitlementQuotas): ComputedRef<QuotaValue | undefined> => {
    return computed(() => quotas.value[key]);
  };

  /**
   * Check if an action is allowed within quota.
   *
   * @param key - The quota key to check
   * @param amount - The amount needed (default: 1)
   * @returns Computed ref that is true if quota allows the action
   */
  const quotaAllows = (key: keyof EntitlementQuotas, amount: number = 1): ComputedRef<boolean> => {
    return computed(() => {
      const q = quotas.value[key];
      if (!q) {
        return true;
      }
      if (q.remaining === null) {
        return true;
      }
      return q.remaining >= amount;
    });
  };

  /**
   * Check if a quota is exhausted.
   *
   * @param key - The quota key to check
   * @returns Computed ref that is true if quota is exhausted (remaining === 0)
   */
  const quotaExhausted = (key: keyof EntitlementQuotas): ComputedRef<boolean> => {
    return computed(() => {
      const q = quotas.value[key];
      if (!q) {
        return false;
      }
      if (q.remaining === null) {
        return false;
      }
      return q.remaining === 0;
    });
  };

  /**
   * Fetch entitlements from the API.
   * Uses cached data by default. Pass true to force refresh.
   */
  const fetchEntitlements = async (forceRefresh: boolean = false) => {
    return store.fetchEntitlements(forceRefresh);
  };

  /**
   * Force refresh entitlements from the API.
   */
  const refreshEntitlements = async () => {
    return store.refreshEntitlements();
  };

  return {
    // State refs
    capabilities,
    limits,
    quotas,
    isLoaded,
    isLoading,
    error,

    // Helper methods that return computed refs
    can,
    limit,
    quota,
    quotaAllows,
    quotaExhausted,

    // Actions
    fetchEntitlements,
    refreshEntitlements,
  };
}
